import { dbStorage } from './indexedDBStorage'
import { fetchAuthSession } from '../api/authAPI'
import type { LedgerSafeEditorIdentity, LedgerSafeHumanContributor } from '../types/ledgerSafeProvenance'

const ACTIVE_EDITOR_PREFERENCE_KEY = 'ledger_safe_active_editor_identity'

const nowIso = (): string => new Date().toISOString()

const generateEditorId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `editor-${crypto.randomUUID()}`
  }
  return `editor-${Date.now()}-${Math.random().toString(16).slice(2)}`
}

const cleanOptional = (value: string | undefined): string | undefined => {
  const trimmed = (value || '').trim()
  return trimmed || undefined
}

const parseEditorIdentity = (rawValue: string | undefined): LedgerSafeEditorIdentity | null => {
  if (!rawValue) {
    return null
  }
  try {
    const parsed = JSON.parse(rawValue) as Partial<LedgerSafeEditorIdentity>
    const editorId = cleanOptional(parsed.editorId)
    const displayName = cleanOptional(parsed.displayName)
    if (!editorId || !displayName) {
      return null
    }
    return {
      editorId,
      displayName,
      email: cleanOptional(parsed.email),
      institution: cleanOptional(parsed.institution),
      orcid: cleanOptional(parsed.orcid),
      createdAt: cleanOptional(parsed.createdAt) || nowIso(),
      updatedAt: cleanOptional(parsed.updatedAt) || nowIso(),
    }
  } catch {
    return null
  }
}

const getStoredEditorIdentity = async (): Promise<LedgerSafeEditorIdentity | null> => {
  const preference = await dbStorage.get('ui_preferences', ACTIVE_EDITOR_PREFERENCE_KEY)
  return parseEditorIdentity(preference?.value)
}

const persistEditorIdentity = async ({
  displayName,
  email,
  institution,
  orcid,
  editorId,
  existing,
}: {
  displayName: string
  email?: string
  institution?: string
  orcid?: string
  editorId?: string
  existing: LedgerSafeEditorIdentity | null
}): Promise<LedgerSafeEditorIdentity> => {
  const timestamp = nowIso()
  const identity: LedgerSafeEditorIdentity = {
    editorId: cleanOptional(editorId) || existing?.editorId || generateEditorId(),
    displayName: cleanOptional(displayName) || existing?.displayName || 'Local editor',
    email: cleanOptional(email),
    institution: cleanOptional(institution),
    orcid: cleanOptional(orcid),
    createdAt: existing?.createdAt || timestamp,
    updatedAt: timestamp,
  }
  await dbStorage.put('ui_preferences', {
    key: ACTIVE_EDITOR_PREFERENCE_KEY,
    value: JSON.stringify(identity),
    updated_at: Date.now(),
  })
  return identity
}

export const getAuthenticatedEditorIdentity = async (): Promise<LedgerSafeEditorIdentity | null> => {
  if (typeof fetch !== 'function') {
    return null
  }
  try {
    const session = await fetchAuthSession()
    if (!session.authenticated || !session.user) {
      return null
    }
    const timestamp = nowIso()
    return {
      editorId: session.user.user_id,
      displayName: session.user.display_name || session.user.email,
      email: session.user.email,
      institution: session.user.domain === 'fu-berlin.de' ? 'Freie Universitat Berlin' : undefined,
      createdAt: timestamp,
      updatedAt: timestamp,
    }
  } catch {
    return null
  }
}

export const getActiveEditorIdentity = async (): Promise<LedgerSafeEditorIdentity | null> => {
  const authenticated = await getAuthenticatedEditorIdentity()
  if (authenticated) {
    return authenticated
  }
  return getStoredEditorIdentity()
}

export const saveActiveEditorIdentity = async ({
  displayName,
  email,
  institution,
  orcid,
  editorId,
}: {
  displayName: string
  email?: string
  institution?: string
  orcid?: string
  editorId?: string
}): Promise<LedgerSafeEditorIdentity> => {
  const existing = await getActiveEditorIdentity()
  return persistEditorIdentity({
    displayName,
    email,
    institution,
    orcid,
    editorId,
    existing,
  })
}

export const ensureActiveEditorIdentity = async (
  fallbackDisplayName = 'Local editor',
): Promise<LedgerSafeEditorIdentity> => {
  const authenticated = await getAuthenticatedEditorIdentity()
  if (authenticated) {
    return authenticated
  }
  const existing = await getStoredEditorIdentity()
  if (existing) {
    return existing
  }
  return persistEditorIdentity({
    displayName: fallbackDisplayName,
    existing: null,
  })
}

export const editorIdentityToContributor = (
  identity: LedgerSafeEditorIdentity,
  role: LedgerSafeHumanContributor['role'] = 'editor',
  contributedAt?: string,
): LedgerSafeHumanContributor => ({
  kind: 'human',
  id: identity.editorId,
  displayName: identity.displayName,
  ...(identity.email ? { email: identity.email } : {}),
  ...(identity.institution ? { institution: identity.institution } : {}),
  ...(identity.orcid ? { orcid: identity.orcid } : {}),
  role,
  ...(contributedAt ? { contributedAt } : {}),
})
